import { useTranslation } from 'react-i18next';
import { ParameterBar } from '@components/atoms/ParameterBar';
import { ParameterDto, RegulationDto, SampleFormat } from '@src/types/types';
import { StyledMarkdown } from '@components/atoms/StyledMarkdown';
import { RegulationsUpselling } from '@components/sample-details/RegulationsUpselling.tsx';
import { useConfigurationContext } from '@hooks/useConfiguration.tsx';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';

export const ParameterListItemExpandedContent = ({
  parameter,
  exceededRegulation,
  status,
}: {
  parameter: ParameterDto;
  exceededRegulation?: RegulationDto;
  status: ParameterStatusType;
}) => {
  const { configuration, isLoading: configIsLoading } =
    useConfigurationContext();
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });

  if (configIsLoading) {
    return null;
  }

  const { nonDetectable, nonDetectableText, notAnalyzable, notAnalyzableText } =
    parameter;

  const renderParameterInfo = (
    isVisible: boolean,
    titleKey: string,
    text?: string,
    defaultText?: string,
  ) => {
    if (!isVisible) {
      return null;
    }
    return (
      <div>
        <h2 className="font-semibold text-lg">{t(titleKey)}</h2>
        <StyledMarkdown markdown={text || defaultText || ''} />
      </div>
    );
  };

  const showBar =
    parameter.format === SampleFormat.NUMERIC &&
    !parameter.regulations.some((regulation) => regulation.lowerLimit);
  return (
    <div
      className="md:px-5 md:pb-5 print:px-5 print:pb-5"
      data-testid="parameter-list-item-expanded-content"
    >
      <div className="p-4 border-t-2 border-grey3light shadow-parameter-list-item-expanded rounded-bonusLarge bg-white">
        {showBar && !notAnalyzable && !nonDetectable && (
          <ParameterBar parameter={parameter} expanded={true} />
        )}
        {renderParameterInfo(
          nonDetectable,
          'notDetectableLong',
          nonDetectableText,
          configuration?.defaultNonDetectableText,
        )}
        {renderParameterInfo(
          notAnalyzable,
          'notAnalyzableLong',
          notAnalyzableText,
          configuration?.defaultNotAnalyzableText,
        )}

        {exceededRegulation && (
          <div className="mt-6">
            {status === ParameterStatusType.ERROR && (
              <h2 className="font-semibold text-error text-lg">
                {t('exceededRegulation')}
              </h2>
            )}
            {status === ParameterStatusType.WARNING && (
              <h2 className="font-semibold text-orange text-lg">
                {t('closeToExceedingRegulation')}
              </h2>
            )}
            <StyledMarkdown
              markdown={
                status === ParameterStatusType.WARNING
                  ? exceededRegulation.closeToLimitExplanation
                  : exceededRegulation.explanation
              }
            />

            <RegulationsUpselling
              upsellingItems={exceededRegulation.upselling}
            />
          </div>
        )}
      </div>
    </div>
  );
};
